/* eslint-disable @woocommerce/dependency-group */
/* global jQuery,finix_google_pay_params,google */
import { useEffect, createElement } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { registerPaymentMethod } from '@woocommerce/blocks-registry';
import { decodeEntities } from '@wordpress/html-entities';
import { getSetting } from '@woocommerce/settings';
import { cartStore } from '@woocommerce/block-data';
import { useSelect } from '@wordpress/data';
import FinixGooglePay from './FinixGooglePay';

const gatewaySlug = 'finix_google_pay_gateway';

const gatewaySettings = getSetting( 'finix_google_pay_gateway_data', {} );

const label = decodeEntities( gatewaySettings.title ) || __( 'Pay with Google Pay', 'finix-for-woocommerce' );

const nonEmptyValue = ( value ) => value && value.trim() !== '';

/**
 * Retrieve shipping data from the checkout form.
 * We use billing data as default as shipping may actually be hidden.
 */
const retrieveShippingData = () => {
	const billing = finix_google_pay_params.billing_data;
	const shipping = finix_google_pay_params.shipping_data;

	// Collect shipping address data.
	return {
		shipping_country: shipping.country || billing.country || '',
		shipping_state: shipping.state || billing.state || '',
		shipping_postcode: shipping.postcode || billing.postcode || '',
		shipping_city: shipping.city || billing.city || '',
		shipping_address_1: shipping.address_1 || billing.address1 || '',
	};
};

/**
 * Validate shipping as a Promise.
 */
const validateShippingData = () => {
	let shippingData = retrieveShippingData();
	shippingData.nonce = finix_google_pay_params.nonce;
	return new Promise( ( resolve ) => {
		fetch( finix_google_pay_params.url.ajax + '?action=validate_shipping_method', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify( shippingData ),
		} )
			.then( ( response ) => response.json() )
			.then( ( response ) => {
				resolve( response.success === true );
			} )
			.catch( () => resolve( false ) );
	} );
};

/**
 * Label component.
 *
 * @param {*} props Props from payment API.
 */
const Label = ( props ) => {
	const { PaymentMethodLabel } = props.components;

	const Icon = () => {
		return createElement( 'img', {
			src: gatewaySettings.icon,
		} );
	};

	return <PaymentMethodLabel text={ label } icon={ <Icon /> } />;
};

const Content = ( props ) => {
	const { eventRegistration, emitResponse } = props;
	const { onPaymentSetup } = eventRegistration;
	const { shippingAddress, billingAddress, totals, coupons, items } = useSelect( ( select ) => select( cartStore ).getCartData(), [] );

	useEffect( () => {
		finix_google_pay_params.shipping_data = shippingAddress;
	}, [ shippingAddress ] );

	useEffect( () => {
		finix_google_pay_params.billing_data = billingAddress;
	}, [ billingAddress ] );

	// When the totals change, update the amount.
	useEffect( () => {
		finix_google_pay_params.amount = ( totals.total_price / 100 ).toFixed( 2 );
		finix_google_pay_params.currency_code = totals.currency_code;
		finix_google_pay_params.subtotal = ( totals.total_items / 100 ).toFixed( 2 );
		finix_google_pay_params.shipping_amount = ( totals.total_shipping / 100 ).toFixed( 2 );
		finix_google_pay_params.tax_amount = ( totals.total_tax / 100 ).toFixed( 2 );
	}, [ totals ] );

	// When the coupons change, update the applied coupons.

	useEffect( () => {
		finix_google_pay_params.coupons = coupons.map( ( coupon ) => ( {
			code: coupon.code,
			total: ( coupon.totals.total_discount / 100 ).toFixed( 2 ),
		} ) );
	}, [ coupons ] );

	// When the items change, update the applied items.
	useEffect( () => {
		finix_google_pay_params.products = items.map( ( item ) => ( {
			name: item.name,
			quantity: item.quantity,
			image: item.images.length && item.images.length > 0 ? item.images[ 0 ].thumbnail : '',
			total: ( item.prices.price * item.quantity / 100 ).toFixed( 2 ),
		} ) );
	}, [ items ] );

	// Triggered on our payment method selection.
	useEffect( () => {
		const paymentsClient = FinixGooglePay.getPaymentsClient();
		paymentsClient
			.isReadyToPay( FinixGooglePay.getIsReadyToPayRequest() )
			.then( ( response ) => {
				if ( response.result ) {
					const checkoutButton = document.querySelector( '#wc-finix-google-pay-form' );
					const googlePayButton = paymentsClient.createButton( {
						onClick: FinixGooglePay.triggerPayment,
						allowedPaymentMethods: [ FinixGooglePay.cardPaymentMethod ],
						buttonColor: finix_google_pay_params.button_color,
						buttonType: finix_google_pay_params.button_type,
						buttonRadius: finix_google_pay_params.button_radius,
						buttonLocale: finix_google_pay_params.button_locale,
					} );
					checkoutButton.appendChild( googlePayButton );
					FinixGooglePay.prefetchPaymentData();
				}
			} )
			.catch( ( error ) => {
				return {
					type: emitResponse.responseTypes.ERROR,
					message: __( 'There was an error while initializing Google Pay. Please check all the details and try again.', 'finix-for-woocommerce' ),
				};
			} );

		// Triggered on "Place Order" button click only.
		const unsubscribe = onPaymentSetup( async () => {
			const isValid = await validateShippingData();

			if ( ! isValid ) {
				return {
					type: emitResponse.responseTypes.ERROR,
					message: __( 'Please make sure shipping and billing information is correct and then try again.', 'finix-for-woocommerce' ),
				};
			}

			try {
				const isPaymentSuccessful = await FinixGooglePay.processPayment();

				if ( isPaymentSuccessful === true ) {
					return {
						type: emitResponse.responseTypes.SUCCESS,
						meta: {
							paymentMethodData: {
								finix_google_pay_success: document.getElementById( 'finix_google_pay_success' ).value,
								finix_google_pay_transaction_id: document.getElementById( 'finix_google_pay_transaction_id' ).value,
								finix_nonce: finix_google_pay_params.nonce,
							},
						},
					};
				} else {
					return {
						type: emitResponse.responseTypes.FAIL,
						message: finix_google_pay_params.text.error_processing,
					};
				}
			} catch ( error ) {
				return {
					type: emitResponse.responseTypes.FAIL,
					message: finix_google_pay_params.text.error_processing,
				};
			}
		} );

		// Clean up the event listener when the component unmounts.
		return unsubscribe;
	}, [ onPaymentSetup ] );

	return createElement(
		'fieldset',
		{
			id: 'wc-' + gatewaySlug + '-form',
			style: { background: 'transparent', padding: '0' },
		},
		// If gatewaySettings.description is not empty, create div > p to display.
		nonEmptyValue( gatewaySettings.description )
			? createElement(
					'div',
					{},
					createElement( 'p', {
						dangerouslySetInnerHTML: {
							__html: gatewaySettings.description,
						},
					} )
			  )
			: null,
		// Create hidden inputs for Google Pay status and transaction id.
		createElement( 'google-pay-button', {
			id: 'finix-google-pay-button',
			buttonstyle: finix_google_pay_params.button_style,
			type: finix_google_pay_params.button_type,
			locale: finix_google_pay_params.button_locale,
		} ),
		createElement( 'input', {
			type: 'hidden',
			name: 'finix_google_pay_success',
			id: 'finix_google_pay_success',
		} ),
		createElement( 'input', {
			type: 'hidden',
			name: 'finix_google_pay_transaction_id',
			id: 'finix_google_pay_transaction_id',
		} )
	);
};

const Edit = () => {};

/**
 * Finix payment method config object.
 *
 * @see https://developer.woocommerce.com/docs/cart-and-checkout-payment-method-integration-for-the-checkout-block/#registration
 */
registerPaymentMethod( {
	name: gatewaySlug,
	label: <Label />,
	content: <Content />,
	edit: <Edit />,
	canMakePayment: () => {
		// Is it configured?
		if ( typeof finix_google_pay_params.google_merchant_id === 'undefined' || finix_google_pay_params.google_merchant_id === '' ) {
			return false;
		}

		// Is Google Pay lib available?
		if ( ! google || ! google.payments ) {
			return false;
		}

		return FinixGooglePay.getPaymentsClient()
			.isReadyToPay( FinixGooglePay.getIsReadyToPayRequest() )
			.then( ( response ) => response.result );
	},
	ariaLabel: label,
	supports: {
		features: gatewaySettings.supports,
	},
} );
